import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Calendar, MapPin, Ticket, Download, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ticketsApi, type TicketDetailResponse } from "@/lib/api";
import { toast } from "sonner";
import { formatNaira } from "@/data/venues";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/events/$eventId/tickets")({
  head: () => ({
    meta: [{ title: "My Tickets — OyoBooking" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: EventTicketsPage,
});

const TIER_LABELS: Record<string, string> = {
  regular: "Regular",
  standard: "Standard",
  early_bird: "Early Bird",
  vip: "VIP",
  vvip: "VVIP",
  table: "Table",
};

const TIER_BADGE: Record<string, string> = {
  regular: "bg-blue-100 text-blue-700",
  standard: "bg-slate-100 text-slate-700",
  early_bird: "bg-rose-100 text-rose-700",
  vip: "bg-amber-100 text-amber-700",
  vvip: "bg-purple-100 text-purple-700",
  table: "bg-green-100 text-green-700",
};

const STATUS_BADGE: Record<string, string> = {
  valid: "bg-green-100 text-green-700",
  used: "bg-slate-100 text-slate-700",
  pending_payment: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-red-100 text-red-700",
};

function EventTicketsPage() {
  const { eventId } = useParams({ from: "/events/$eventId/tickets" });
  const { isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState<TicketDetailResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      setError("Please sign in to view your tickets.");
      return;
    }
    loadTickets();
  }, [eventId, isAuthenticated]);

  async function loadTickets() {
    setLoading(true);
    setError(null);
    try {
      const res = await ticketsApi.getByEvent(eventId);
      setTickets(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load tickets";
      setError(msg);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }

  function downloadQr(ticket: TicketDetailResponse) {
    if (!ticket.qrCodeImage) return;
    const link = document.createElement("a");
    link.href = ticket.qrCodeImage;
    link.download = `ticket-${ticket.ticketRef}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="size-12 text-red-500 mx-auto" />
          <h2 className="font-display text-xl font-bold">Could not load tickets</h2>
          <p className="text-muted-foreground">{error}</p>
          <Link to="/events"><Button className="rounded-full">Browse events</Button></Link>
        </div>
      </div>
    );
  }

  const eventName = tickets[0]?.eventName ?? "Event";
  const eventDate = tickets[0]?.eventDate ? new Date(tickets[0].eventDate) : null;
  const venueName = tickets[0]?.venueName ?? "";

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/events/$eventId" params={{ eventId }} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Back to event
          </Link>
        </div>

        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Ticket className="size-6 text-primary" /> My Tickets
          </h1>
          <p className="text-muted-foreground">{eventName}</p>
          {eventDate && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Calendar className="size-4" /> {eventDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
              {venueName && <span className="flex items-center gap-1"><MapPin className="size-4" /> {venueName}</span>}
            </div>
          )}
        </div>

        {tickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">You do not have any tickets for this event yet.</p>
              <Link to="/events/$eventId" params={{ eventId }} className="mt-4 inline-block">
                <Button className="rounded-full">Get tickets</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="overflow-hidden">
                <CardHeader className="bg-muted/30 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_BADGE[ticket.tierType] ?? "bg-muted text-muted-foreground"}`}>
                        {TIER_LABELS[ticket.tierType] ?? ticket.tierName}
                      </span>
                      <CardTitle className="text-lg font-semibold">{ticket.ticketRef}</CardTitle>
                      <p className="text-sm text-muted-foreground">{ticket.attendeeName}</p>
                    </div>
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[ticket.status] ?? "bg-muted text-muted-foreground"}`}>
                      {ticket.status.replace("_", " ")}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="py-6">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    {ticket.qrCodeImage ? (
                      <div className="rounded-xl border bg-white p-3">
                        <img src={ticket.qrCodeImage} alt={`QR code for ticket ${ticket.ticketRef}`} className="size-40 object-contain" />
                      </div>
                    ) : (
                      <div className="size-40 rounded-xl border bg-muted flex items-center justify-center text-muted-foreground text-sm text-center p-4">
                        QR code unavailable
                      </div>
                    )}
                    <div className="flex-1 space-y-3 text-center md:text-left">
                      <p className="text-sm text-muted-foreground">
                        Present this QR code at the venue entrance. A venue manager will scan it to verify your ticket.
                      </p>
                      <p className="text-xs font-mono text-muted-foreground">{ticket.ticketCode}</p>
                      {ticket.status === "valid" && !ticket.isCheckedIn && (
                        <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-green-600">
                          <CheckCircle2 className="size-4" /> Ready for check-in
                        </div>
                      )}
                      {ticket.isCheckedIn && (
                        <div className="text-sm text-slate-600">
                          Checked in at {ticket.checkedInAt ? new Date(ticket.checkedInAt).toLocaleString() : "—"}
                        </div>
                      )}
                      {ticket.qrCodeImage && (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadQr(ticket)}>
                          <Download className="size-4 mr-2" /> Download QR
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
